/**
 * Dashboard — the customer portal home. Lists the customer's properties
 * (masters) and the sub-projects under each, with status and total. From here
 * they start a new project (at an existing address or a new one) and log out.
 *
 * Post-payment "Done"/"Start another quote" route here. If the customer has no
 * master yet, we send them to intake.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDashboard, clearToken, type DashboardMaster } from '../lib/api';

export function Dashboard(): JSX.Element {
  const navigate = useNavigate();
  const [masters, setMasters] = useState<DashboardMaster[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    getDashboard()
      .then((m) => {
        if (!active) return;
        if (m.length === 0) {
          navigate('/intake', { replace: true });
          return;
        }
        setMasters(m);
      })
      .catch(() => {
        if (active) setError('We couldn\u2019t load your dashboard just now.');
      });
    return () => {
      active = false;
    };
  }, [navigate]);

  function startAt(masterId: string): void {
    // Don't pre-create the project (that orphaned empty OPEN rows). Carry the
    // masterId into the service picker; the project is created — with a
    // customer-chosen name — on the name-your-project screen.
    navigate('/service', { state: { masterId } });
  }

  function openProject(projectId: string, status: string): void {
    // SUBMITTED/ACCEPTED → read-only summary; OPEN → resume at the summary where
    // they can add more, set budget/timeline, and submit.
    navigate('/design/project', { state: { projectId, readOnly: status !== 'OPEN' } });
  }

  function logout(): void {
    clearToken();
    navigate('/');
  }

  if (error) {
    return (
      <div className="flow flow--single">
        <div className="stage-card">
          <div className="pb-note pb-note--flag" role="alert">{error}</div>
          <div className="q-actions"><button type="button" className="pb-btn" onClick={logout}>Sign out</button></div>
        </div>
      </div>
    );
  }

  if (!masters) {
    return (
      <div className="flow flow--single">
        <div className="stage-card"><p className="stage-card__lede">Loading your projects&hellip;</p></div>
      </div>
    );
  }

  return (
    <div className="flow flow--single">
      <div className="stage-card">
        <h1>Your projects</h1>

        {masters.map((m) => (
          <div key={m.id} className="pb-surface" style={{ marginTop: 'var(--pb-3)' }}>
            <div className="pb-option__label">{m.fullName}</div>
            <div className="estimate__hint">{m.addressLine1}, {m.city} {m.state} {m.postalCode}</div>

            <div style={{ marginTop: 'var(--pb-3)' }}>
              {m.projects.length === 0 ? (
                <div className="estimate__hint">No projects yet.</div>
              ) : (
                m.projects.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className="pb-option"
                    onClick={() => openProject(p.id, p.status)}
                    style={{ width: '100%', textAlign: 'left' }}
                  >
                    <span style={{ flexGrow: 1 }}>
                      <span className="pb-option__label">{p.name ?? 'Project'}</span>
                      <span className="pb-option__sub">
                        {p.status === 'SENT'
                          ? 'Estimated \u2014 tap to view your quote'
                          : p.status === 'SUBMITTED'
                            ? 'Submitted \u2014 estimate on the way'
                            : p.status === 'ACCEPTED'
                              ? 'Accepted'
                              : 'In progress \u2014 tap to finish'}
                      </span>
                    </span>
                    <span style={{ fontFamily: 'var(--pb-font-display)', fontSize: 'var(--pb-size-small)' }}>
                      {p.status}
                    </span>
                  </button>
                ))
              )}
            </div>

            <div className="q-actions" style={{ marginTop: 'var(--pb-2)' }}>
              <button type="button" className="pb-btn" onClick={() => startAt(m.id)}>
                Start a new project here
              </button>
            </div>
          </div>
        ))}

        <div className="q-actions" style={{ marginTop: 'var(--pb-4)' }}>
          <button type="button" className="pb-btn pb-btn--ghost" onClick={() => navigate('/intake')}>
            Add another property
          </button>
        </div>
      </div>
    </div>
  );
}
